import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SmartintegService } from '../../../../services/smartinteg.service';
import { ToastService } from '../../../../services/toast.service';
import {
  TipoProvedor,
  TipoCredencial,
  Provedor,
  ProvedorConexao,
  CreateProvedorCompleto
} from '../../../../types/smartinteg';

@Component({
  selector: 'app-provedor-form',
  templateUrl: './provedor-form.component.html',
  styleUrl: './provedor-form.component.css'
})
export class ProvedorFormComponent implements OnInit {
  form!: FormGroup;
  loading: boolean = false;
  isEditMode: boolean = false;
  provedorId?: string;

  // Enums para dropdowns
  TipoProvedor = TipoProvedor;
  TipoCredencial = TipoCredencial;

  // Opções para dropdowns
  tiposProvedor = [
    { label: 'Banco de Dados', value: TipoProvedor.BASEDADOS },
    { label: 'API REST', value: TipoProvedor.APIREST }
  ];

  tiposCredencial = [
    { label: 'Sem Credencial', value: TipoCredencial.SEM_CREDENCIAL },
    { label: 'Basic Auth', value: TipoCredencial.BASIC },
    { label: 'Bearer Token', value: TipoCredencial.BEARER },
    { label: 'API Key', value: TipoCredencial.API_KEY },
    { label: 'OAuth2', value: TipoCredencial.OAUTH2 },
    { label: 'Database', value: TipoCredencial.DATABASE }
  ];

  tiposBancoDados = [
    { label: 'Oracle', value: 'oracle' },
    { label: 'PostgreSQL', value: 'postgres' },
    { label: 'MySQL', value: 'mysql' },
    { label: 'SQL Server', value: 'sqlserver' }
  ];

  constructor(
    private fb: FormBuilder,
    private smartintegService: SmartintegService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.inicializarFormulario();

    // Verificar se está em modo de edição
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.provedorId = params['id'];
        if (this.provedorId) {
          this.carregarProvedor(this.provedorId);
        }
      }
    });

    // Observar mudanças no tipo de provedor
    this.form.get('tipo')?.valueChanges.subscribe((tipo: TipoProvedor) => {
      this.ajustarCamposPorTipo(tipo);
    });

    // Observar mudanças no tipo de credencial
    this.form.get('tipoCredencial')?.valueChanges.subscribe((tipoCredencial: TipoCredencial) => {
      this.ajustarCamposPorCredencial(tipoCredencial);
    });
  }

  inicializarFormulario(): void {
    this.form = this.fb.group({
      // Provedor
      nome: ['', [Validators.required, Validators.minLength(3)]],
      descricao: ['', Validators.required],
      tipo: [TipoProvedor.BASEDADOS, Validators.required],

      // Conexão
      nomeConexao: ['', [Validators.required, Validators.minLength(3)]],
      ambiente: ['', Validators.required],
      urlBase: [''],
      padrao: [false],

      // Configurações
      tipo_bd: [''],
      host: [''],
      porta: [null],
      database: [''],
      timeout: [30000],
      retry_tentativas: [3],
      retry_intervalo: [1000],

      // Credenciais
      tipoCredencial: [TipoCredencial.DATABASE, Validators.required],
      cred_user: [''],
      cred_password: [''],
      cred_username: [''],
      cred_token: [''],
      cred_apiKey: [''],
      cred_header: ['']
    });
  }

  carregarProvedor(id: string): void {
    this.loading = true;
    this.smartintegService.buscarProvedor(id).subscribe({
      next: (provedor: Provedor) => {
        // Preencher dados do provedor
        this.form.patchValue({
          nome: provedor.nome,
          descricao: provedor.descricao,
          tipo: provedor.tipo
        });

        // Carregar conexão padrão do provedor
        this.carregarConexaoPadrao(id);
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao carregar provedor: ' + error.error?.message);
        this.loading = false;
        this.voltar();
      }
    });
  }

  carregarConexaoPadrao(provedorId: string): void {
    this.smartintegService.listarConexoesPorProvedor(provedorId).subscribe({
      next: (conexoes: ProvedorConexao[]) => {
        const conexaoPadrao = conexoes.find(c => c.padrao) || conexoes[0];

        if (conexaoPadrao) {
          this.form.patchValue({
            nomeConexao: conexaoPadrao.nome,
            ambiente: conexaoPadrao.ambiente,
            urlBase: conexaoPadrao.urlBase || '',
            padrao: conexaoPadrao.padrao,

            tipo_bd: conexaoPadrao.config?.tipo_bd || '',
            host: conexaoPadrao.config?.host || '',
            porta: conexaoPadrao.config?.porta || null,
            database: conexaoPadrao.config?.database || '',
            timeout: conexaoPadrao.config?.timeout || 30000,
            retry_tentativas: conexaoPadrao.config?.retry?.tentativas || 3,
            retry_intervalo: conexaoPadrao.config?.retry?.intervalo || 1000,

            tipoCredencial: conexaoPadrao.tipoCredencial
          });

          // Preencher credenciais (não temos os valores do backend por segurança)
          // O usuário precisará preencher novamente se quiser alterar
        }

        this.loading = false;
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao carregar conexão: ' + error.error?.message);
        this.loading = false;
      }
    });
  }

  ajustarCamposPorTipo(tipo: TipoProvedor): void {
    const urlBaseControl = this.form.get('urlBase');
    const tipo_bdControl = this.form.get('tipo_bd');
    const hostControl = this.form.get('host');
    const portaControl = this.form.get('porta');
    const databaseControl = this.form.get('database');

    if (tipo === TipoProvedor.APIREST) {
      // API REST: URL é obrigatória, campos de BD são opcionais
      urlBaseControl?.setValidators([Validators.required]);
      tipo_bdControl?.clearValidators();
      hostControl?.clearValidators();
      portaControl?.clearValidators();
      databaseControl?.clearValidators();
    } else {
      // BASEDADOS: Campos de BD são obrigatórios, URL é opcional
      urlBaseControl?.clearValidators();
      tipo_bdControl?.setValidators([Validators.required]);
      hostControl?.setValidators([Validators.required]);
      portaControl?.setValidators([Validators.required]);
      databaseControl?.setValidators([Validators.required]);
    }

    urlBaseControl?.updateValueAndValidity();
    tipo_bdControl?.updateValueAndValidity();
    hostControl?.updateValueAndValidity();
    portaControl?.updateValueAndValidity();
    databaseControl?.updateValueAndValidity();
  }

  ajustarCamposPorCredencial(tipoCredencial: TipoCredencial): void {
    // Limpar validadores de todos os campos de credencial
    const camposCredencial = ['cred_user', 'cred_password', 'cred_username', 'cred_token', 'cred_apiKey', 'cred_header'];
    camposCredencial.forEach(campo => {
      this.form.get(campo)?.clearValidators();
      this.form.get(campo)?.updateValueAndValidity();
    });

    // Adicionar validadores conforme o tipo de credencial
    switch (tipoCredencial) {
      case TipoCredencial.DATABASE:
        this.form.get('cred_user')?.setValidators([Validators.required]);
        this.form.get('cred_password')?.setValidators([Validators.required]);
        break;
      case TipoCredencial.BASIC:
        this.form.get('cred_username')?.setValidators([Validators.required]);
        this.form.get('cred_password')?.setValidators([Validators.required]);
        break;
      case TipoCredencial.BEARER:
        this.form.get('cred_token')?.setValidators([Validators.required]);
        break;
      case TipoCredencial.API_KEY:
        this.form.get('cred_apiKey')?.setValidators([Validators.required]);
        this.form.get('cred_header')?.setValidators([Validators.required]);
        break;
      case TipoCredencial.OAUTH2:
        // OAuth2 geralmente requer configuração mais complexa
        // Por enquanto não adiciona validadores específicos
        break;
      case TipoCredencial.SEM_CREDENCIAL:
        // Nenhum campo obrigatório
        break;
    }

    // Atualizar validações
    camposCredencial.forEach(campo => {
      this.form.get(campo)?.updateValueAndValidity();
    });
  }

  mostrarCamposBancoDados(): boolean {
    return this.form.get('tipo')?.value === TipoProvedor.BASEDADOS;
  }

  mostrarCampoApiRest(): boolean {
    return this.form.get('tipo')?.value === TipoProvedor.APIREST;
  }

  mostrarCredencialDatabase(): boolean {
    return this.form.get('tipoCredencial')?.value === TipoCredencial.DATABASE;
  }

  mostrarCredencialBasic(): boolean {
    return this.form.get('tipoCredencial')?.value === TipoCredencial.BASIC;
  }

  mostrarCredencialBearer(): boolean {
    return this.form.get('tipoCredencial')?.value === TipoCredencial.BEARER;
  }

  mostrarCredencialApiKey(): boolean {
    return this.form.get('tipoCredencial')?.value === TipoCredencial.API_KEY;
  }

  mostrarCredencialOAuth2(): boolean {
    return this.form.get('tipoCredencial')?.value === TipoCredencial.OAUTH2;
  }

  salvar(): void {
    if (this.form.invalid) {
      this.toastService.alerta('Atenção', 'Preencha todos os campos obrigatórios');
      this.marcarCamposComoTocados();
      return;
    }

    this.loading = true;

    if (this.isEditMode) {
      this.atualizar();
    } else {
      this.criar();
    }
  }

  criar(): void {
    const formValue = this.form.value;

    const data: CreateProvedorCompleto = {
      nome: formValue.nome,
      descricao: formValue.descricao,
      tipo: formValue.tipo,
      nomeConexao: formValue.nomeConexao,
      ambiente: formValue.ambiente,
      urlBase: formValue.urlBase || undefined,
      padrao: formValue.padrao,
      config: {
        tipo_bd: formValue.tipo_bd || undefined,
        host: formValue.host || undefined,
        porta: formValue.porta || undefined,
        database: formValue.database || undefined,
        timeout: formValue.timeout || undefined,
        retry: {
          tentativas: formValue.retry_tentativas || 3,
          intervalo: formValue.retry_intervalo || 1000
        }
      },
      tipoCredencial: formValue.tipoCredencial,
      credenciais: {
        user: formValue.cred_user || undefined,
        password: formValue.cred_password || undefined,
        username: formValue.cred_username || undefined,
        token: formValue.cred_token || undefined,
        apiKey: formValue.cred_apiKey || undefined,
        header: formValue.cred_header || undefined
      }
    };

    this.smartintegService.criarProvedorCompleto(data).subscribe({
      next: () => {
        this.toastService.sucesso('Sucesso', 'Provedor criado com sucesso');
        this.loading = false;
        this.voltar();
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao criar provedor: ' + error.error?.message);
        this.loading = false;
      }
    });
  }

  atualizar(): void {
    const formValue = this.form.value;

    const data: Provedor = {
      id: this.provedorId!,
      nome: formValue.nome,
      descricao: formValue.descricao,
      tipo: formValue.tipo,
      criadoPor: '', // Será ignorado pelo backend
      dtCriado: new Date() // Será ignorado pelo backend
    };

    this.smartintegService.atualizarProvedor(data).subscribe({
      next: () => {
        this.toastService.sucesso('Sucesso', 'Provedor atualizado com sucesso');
        this.loading = false;
        this.voltar();
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao atualizar provedor: ' + error.error?.message);
        this.loading = false;
      }
    });
  }

  marcarCamposComoTocados(): void {
    Object.keys(this.form.controls).forEach(key => {
      this.form.get(key)?.markAsTouched();
    });
  }

  campoInvalido(campo: string): boolean {
    const control = this.form.get(campo);
    return !!(control && control.invalid && control.touched);
  }

  voltar(): void {
    this.router.navigate(['/smartinteg/provedores']);
  }

  limpar(): void {
    this.form.reset({
      tipo: TipoProvedor.BASEDADOS,
      tipoCredencial: TipoCredencial.DATABASE,
      padrao: false,
      timeout: 30000,
      retry_tentativas: 3,
      retry_intervalo: 1000
    });
  }
}
